import { useQuery } from 'react-query';
import { FeaturedJob } from 'rwl-shared/dist/models';
import { useAPIRoute, useV2APIRoute } from '../../hooks/useAPIRoute';
import { useGlobal } from '../../hooks';
import { useSelectedPostCode } from '../../hooks/useSelectedPostCode';
import { fetchV2FeaturedJobById } from '../v2/fetchV2FeaturedContent';

type UseFetchJobParams = {
    jobId?: string | undefined;
    enabled?: boolean;
};

export const useFetchJob = ({ jobId, enabled }: UseFetchJobParams) => {
    const { accountId } = useGlobal();
    const url = useAPIRoute('serviceareas/featured/job', 'v1');
    const v2ServiceAreaContentUrl = useV2APIRoute('serviceArea/content');
    // Selected area, needed to page the v2 content — v2 has no global by-id
    // lookup. Read from the lifted context because this hook runs above
    // PlacesAutocompleteProvider (see useSelectedPostCode).
    const postCode = useSelectedPostCode();

    return useQuery<FeaturedJob>(
        // v2 is part of the key so flipping the flag cannot serve a response the
        // other backend produced. postCode belongs in the key only when v2 is
        // active (it resolves the job within the selected area) — folding it in
        // for v1 would invalidate an already-cached by-id result every time the
        // visitor picks a new place, flashing a needless reload of the pop-out.
        [
            'useFetchJob',
            accountId,
            jobId,
            v2ServiceAreaContentUrl ? postCode : undefined,
            v2ServiceAreaContentUrl ? 'v2' : 'v1',
        ],
        async () => {
            if (!jobId || !accountId) {
                return undefined;
            }

            if (v2ServiceAreaContentUrl) {
                const v2Job = await fetchV2FeaturedJobById({
                    serviceAreaContentUrl: v2ServiceAreaContentUrl,
                    accountId,
                    postCode,
                    jobId,
                });
                // Undefined means v2 could not resolve it (no selected area, the
                // job is past the stored cap, or a page failed) — fall back to
                // the v1 by-id endpoint.
                if (v2Job) {
                    return v2Job;
                }
            }

            try {
                const result = await fetch(
                    `${url}?` +
                        new URLSearchParams({
                            accountId: accountId,
                            jobId: jobId,
                        }),
                    {
                        method: 'GET',
                    }
                );

                return result.json();
            } catch (e) {
                console.error('useFetchJob error:', e);
                throw e;
            }
        },
        {
            enabled: !!jobId && !!accountId && enabled,
            staleTime: 1000 * 60 * 60 * 24, // 24 hours
            cacheTime: 1000 * 10,
        }
    );
};
